import {
  HeaderMedia as HeaderMediaItem,
  MediaDisplay,
} from "../../../../resources/modelTypes";
import couplePhotoWideDefault from "../../../images/website/photo-couple-wide.png";
import couplePhotoDefault from "../../../images/website/photo-couple.png";
import singlePhotoDefault from "../../../images/website/photo-single.png";
import { cloudinaryUrl } from "../../../utils";
import { FullBleedMedia } from "./FullBleedMedia";
import { LandscapeMedia } from "./LandscapeMedia";
import { MarqueeMedia } from "./MarqueeMedia";
import { OverlappingMedia } from "./OverlappingMedia";
import { SideBySideMedia } from "./SideBySideMedia";
import { SlideshowMedia } from "./SlideshowMedia";
import { SquareMedia } from "./SquareMedia";
import {
  filterToPhotoOnlyHeaderMedia,
  getHeaderMediaAtPosition,
  getHeaderMediaFrames,
  getOrderedHeaderMedia,
  getPrimaryHeaderSlotPosition,
  resolveHeaderMediaUrl,
} from "./getPrimaryHeaderMediaUrl";
import React, { memo } from "react";

export type HeaderMediaProps = {
  alt: string;
  headerMedia: HeaderMediaItem[];
  mediaDisplay: MediaDisplay;
};

/** What Landscape/FullBleed need to decide between rendering a <video> or an <img>. */
type ResolvedPrimaryMedia = {
  mediaType: "photo" | "video";
  photoURL: string;
  posterURL?: string;
  videoURL?: string;
};

/**
 * The primary (first-by-position) header media for Landscape/FullBleed, the two
 * layouts that support video, so video placements are deliberately not filtered
 * out. A playable video URL is built from publicId, as in DisplayPhotoOrVideo.
 */
const resolvePrimaryMedia = (
  headerMedia: HeaderMediaItem[],
  defaultPhotoURL: string,
): ResolvedPrimaryMedia => {
  const primary = getOrderedHeaderMedia(headerMedia)[0];

  if (!primary) {
    return { mediaType: "photo", photoURL: defaultPhotoURL };
  }

  if (primary.mediaType === "video" && primary.publicId) {
    return {
      mediaType: "video",
      photoURL: defaultPhotoURL,
      posterURL: cloudinaryUrl(
        primary.publicId,
        "jpg",
        "so_auto/h_2048,w_2048,c_fit",
      ),
      videoURL: cloudinaryUrl(primary.publicId, "mp4", "q_auto"),
    };
  }

  return {
    mediaType: "photo",
    photoURL: resolveHeaderMediaUrl(primary) || defaultPhotoURL,
  };
};

/**
 * Renders the modular header media block for the selected mediaDisplay.
 * All MediaDisplay treatments are implemented; None renders nothing.
 * Empty slots show stock placeholders, never legacy site.assets images:
 * new-layouts sites are migrated onto headerMedia rows.
 */
const HeaderMedia = ({ alt, headerMedia, mediaDisplay }: HeaderMediaProps) => {
  if (mediaDisplay === MediaDisplay.None) {
    return null;
  }

  if (
    mediaDisplay === MediaDisplay.Square ||
    mediaDisplay === MediaDisplay.Overlapping ||
    mediaDisplay === MediaDisplay.SideBySide
  ) {
    // Photo-only layouts: drop video rows before the position lookup so a
    // stray one can't occupy a slot.
    const photoOnlyHeaderMedia = filterToPhotoOnlyHeaderMedia(headerMedia);
    const firstHeaderMedia = getHeaderMediaAtPosition(photoOnlyHeaderMedia, 0);
    const secondHeaderMedia = getHeaderMediaAtPosition(photoOnlyHeaderMedia, 1);
    const firstPhotoURL =
      (firstHeaderMedia && resolveHeaderMediaUrl(firstHeaderMedia)) ||
      singlePhotoDefault;
    const secondPhotoURL =
      (secondHeaderMedia && resolveHeaderMediaUrl(secondHeaderMedia)) ||
      singlePhotoDefault;

    if (mediaDisplay === MediaDisplay.Overlapping) {
      return (
        <OverlappingMedia
          alt={alt}
          backPhotoURL={firstPhotoURL}
          frontPhotoURL={secondPhotoURL}
        />
      );
    }

    if (mediaDisplay === MediaDisplay.SideBySide) {
      return (
        <SideBySideMedia
          alt={alt}
          leftPhotoURL={firstPhotoURL}
          rightPhotoURL={secondPhotoURL}
        />
      );
    }

    return (
      <SquareMedia
        alt={alt}
        leftPhotoURL={firstPhotoURL}
        rightPhotoURL={secondPhotoURL}
      />
    );
  }

  if (
    mediaDisplay === MediaDisplay.Marquee ||
    mediaDisplay === MediaDisplay.Slideshow
  ) {
    // Marquee/Slideshow are also photo-only — same filter-before-lookup rule.
    const frames = getHeaderMediaFrames(
      filterToPhotoOnlyHeaderMedia(headerMedia),
    );
    const resolvedFrames =
      frames.length > 0 ? frames : [{ position: 0, url: couplePhotoDefault }];

    if (mediaDisplay === MediaDisplay.Marquee) {
      return <MarqueeMedia alt={alt} frames={resolvedFrames} />;
    }

    return <SlideshowMedia alt={alt} frames={resolvedFrames} />;
  }

  // Landscape/FullBleed support video — no photo-only filtering here.
  const primaryHeaderSlot = getPrimaryHeaderSlotPosition(headerMedia);
  // Each stock filler matches its layout's aspect ratio so object-fit: cover
  // doesn't crop its border: 3:2 for FullBleed, 24:13 for Landscape.
  const defaultPhotoURL =
    mediaDisplay === MediaDisplay.FullBleed
      ? couplePhotoDefault
      : couplePhotoWideDefault;
  const resolvedPrimaryMedia = resolvePrimaryMedia(
    headerMedia,
    defaultPhotoURL,
  );

  switch (mediaDisplay) {
    case MediaDisplay.FullBleed:
      return (
        <FullBleedMedia
          alt={alt}
          headerSlotPosition={primaryHeaderSlot}
          mediaType={resolvedPrimaryMedia.mediaType}
          photoURL={resolvedPrimaryMedia.photoURL}
          posterURL={resolvedPrimaryMedia.posterURL}
          videoURL={resolvedPrimaryMedia.videoURL}
        />
      );
    case MediaDisplay.Landscape:
    default:
      return (
        <LandscapeMedia
          alt={alt}
          headerSlotPosition={primaryHeaderSlot}
          mediaType={resolvedPrimaryMedia.mediaType}
          photoURL={resolvedPrimaryMedia.photoURL}
          posterURL={resolvedPrimaryMedia.posterURL}
          videoURL={resolvedPrimaryMedia.videoURL}
        />
      );
  }
};

const MemoizedHeaderMedia = memo(HeaderMedia);

export { MemoizedHeaderMedia as HeaderMedia };
